import { ChangeEvent, SyntheticEvent, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { useMutation } from '@apollo/client';
import BootstrapForm from 'react-bootstrap/Form';
import startCase from 'lodash/startCase';

import * as K from 'common/constants';
import { UrlParams } from 'common/types';
import ELEMENT from 'common/element';
import { Permission, SavedSearch, SavedSearchType } from 'common/graphqlTypes';
import { buildUrl, getScalyrPath, updateUrlParams } from 'common/util/location';

import Dialog from 'components/common/Dialog/Dialog';
import { SAVE_SEARCH } from 'components/search/queries';
import { hasPermissionState, SavedSearches, savedSearchesState } from 'components/account/account.state';
import { DELETE_SEARCH } from 'components/other/MainNavBar/queries';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import { absoluteResolvedUrlState, searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import ComboBox from 'components/common/ComboBox/ComboBox';

import './SaveSearchDialog.scss';

type SaveSearchDialogProps = {
  show:                boolean;
  close:               () => void;
  searchType:          SavedSearchType;
  action?:             K.ACTION;
  savedSearchToEdit?:  SavedSearch;
  savedSearchUrl?:     string;
};

type SEARCH_ROUTE = K.ROUTE.EVENTS | K.ROUTE.GRAPH | K.ROUTE.DISTRIBUTION | K.ROUTE.TRACE | K.ROUTE.POWER_QUERY;

// figure out what we're saving based on where we are
const SEARCH_TYPE_BY_ROUTE: Record<SEARCH_ROUTE, string> = {
  [K.ROUTE.EVENTS]:       'search',
  [K.ROUTE.GRAPH]:        'graph',
  [K.ROUTE.DISTRIBUTION]: 'graph',
  [K.ROUTE.TRACE]:        'trace',
  [K.ROUTE.POWER_QUERY]:  'power query',
};

const DEFAULT_SAVED_SEARCH = {
  name: '',
  url:  '',
  type: SavedSearchType.PRIVATE,
  index: 0,
} as SavedSearch;

/**
 *
 * This component allows the user to save or edit a search as a personal or shared search. The code can be
 * a bit tricky for two reasons:
 *
 * 1. Type is part of a saved search, but it's passed in and managed separately.
 * 2. This dialog handles both creating a new search, and editing an existing one.
 *
 * Saving an edited search is done in two steps: delete then create. Once we have an API for editing a search,
 * that will be cleaned up.
 *
 * @prop show                  if true, the dialog is displayed
 * @prop close                 function to call to close the dialog
 * @prop searchType            private or shared
 * @prop action                SAVE or EDIT
 * @prop savedSearchToEdit     saved search to edit
 * @prop savedSearchUrl        URL to save if it's not the same as the current URL
 */
function SaveSearchDialog({
  show,
  close,
  searchType,
  action = K.ACTION.SAVE,
  savedSearchToEdit,
  savedSearchUrl,
}: SaveSearchDialogProps): JSX.Element {

  const hasUserAccessPermission = useRecoilValue(hasPermissionState(Permission.userConfigWrite));
  if (!hasUserAccessPermission) {
    searchType = SavedSearchType.PRIVATE;
  }

  // these two search fields are managed as state; the search type is managed as a prop
  const [ savedName, setSavedName ] = useState('');
  const [ savedUrl, setSavedUrl ] = useState('');
  const [ savedType, setSavedType ] = useState(searchType);

  const [ savedSearches, setSavedSearches ] = useRecoilState(savedSearchesState);
  const savedSearchNames = savedSearches[savedType].map((search) => search.name);

  // we need a stable ref to avoid closing over initial values, so we create an object and update its properties
  const editedSearch = useRef<SavedSearch>(DEFAULT_SAVED_SEARCH);

  // save a search, and update the list for its type
  const [saveSearch] = useMutation(SAVE_SEARCH, {
    onCompleted: (data : {saveSearchV2 : SavedSearches[]}) => {
      // @ts-expect-error
      setSavedSearches({[savedType]: data?.saveSearchV2});
    },
  });

  const searchParams = useRecoilValue(searchParamRecordState);
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);

  const isEdit = (action === K.ACTION.EDIT) && !!savedSearchToEdit;

  // saving an edited search happens via delete/create until we have an edit API
  const [ editSearch ] = useMutation(DELETE_SEARCH,
    {
      onCompleted: (data : {deleteSavedSearchV2 : SavedSearches[]}) => {
        setSavedType(editedSearch.current.type);
        const deletedType = savedSearchToEdit?.type as SavedSearchType;
        if (deletedType) {
          // @ts-expect-error
          setSavedSearches({[deletedType]: data?.deleteSavedSearchV2});
        }
        saveSearch({
          variables: {
            name: editedSearch.current.name,
            type: editedSearch.current.type,
            url:  editedSearch.current.url,
          },
        });
      },
    });

  const titleParts = [ isEdit ? 'Edit' : 'Save' ];
  if (savedType === SavedSearchType.SHARED) {
    titleParts.push('Shared');
  }
  const url = new URL(savedSearchUrl || absoluteUrl);
  const urlToSave = buildUrl(
    url.pathname,
    updateUrlParams(
      url.search,
      {
        [K.URL_PARAM.START_TIME]: searchParams[K.URL_PARAM.START_TIME],
        [K.URL_PARAM.END_TIME]:   searchParams[K.URL_PARAM.END_TIME],
      } as UrlParams,
    ),
  );
  const route = getScalyrPath(url) as SEARCH_ROUTE;
  const savedThing = SEARCH_TYPE_BY_ROUTE[route] || 'search';
  titleParts.push(startCase(savedThing));
  const dialogTitle = titleParts.join(' ');

  // update state of search type if prop changes
  const prevSearchType = usePrevious(searchType);
  useConditionalEffect(() => {
    setSavedType(searchType);
  }, searchType !== prevSearchType);

  const prevSavedSearchToEdit = usePrevious(savedSearchToEdit);
  useConditionalEffect(() => {
    if (savedSearchToEdit) {
      // editing a saved search - update state based on that saved search
      setSavedName(savedSearchToEdit.name);
      setSavedUrl(savedSearchToEdit.url);
    }
  }, savedSearchToEdit !== prevSavedSearchToEdit);

  // clear state before we close
  const closeAndCleanUp = (): void => {
    setSavedName('');
    setSavedUrl('');
    close();
  };

  // save the search and close the dialog
  const onSubmit = (): void => {

    const existingSearchToEdit = isEdit ? undefined : savedSearches[savedType].find((search) => search.name === savedName);

    if (isEdit || existingSearchToEdit) {
      editedSearch.current.name = savedName;
      editedSearch.current.url = isEdit ? savedUrl : urlToSave;
      editedSearch.current.index = savedSearchToEdit?.index || existingSearchToEdit?.index || -1;
      editedSearch.current.type = savedType;
    }

    // new search
    let variables = {
      name: savedName,
      type: savedType,
      url:  urlToSave,
    } as SavedSearch;

    if (isEdit && savedSearchToEdit) {
      // search to delete (passed as prop)
      variables = {
        name:  savedSearchToEdit.name,
        url:   savedSearchToEdit.url,
        type:  savedSearchToEdit.type,
        index: savedSearchToEdit.index,
      };
    } else if (existingSearchToEdit) {
      // search to delete (as we're saving over it)
      variables = {
        name:  existingSearchToEdit.name,
        url:   existingSearchToEdit.url,
        type:  savedType,
        index: existingSearchToEdit.index,
      };
    }

    if (isEdit || existingSearchToEdit) {
      editSearch({ variables });
    } else {
      saveSearch({ variables });
    }
    closeAndCleanUp();
  };

  const onNameChange = (newName: string): void => {
    setSavedName(newName);
    editedSearch.current.name = newName;
  };

  const onUrlChange = (ev: SyntheticEvent): void => {
    const newUrl = (ev.target as HTMLInputElement).value;
    setSavedUrl(newUrl);
    editedSearch.current.url = newUrl;
  };

  const addToOptions = [
    {
      label:  'My search menu',
      value:  SavedSearchType.PRIVATE,
      testId: ELEMENT.saveSearchDialogPrivate,
    },
    {
      label:  'Everyone\'s search menu (for this ' + K.PRODUCT_NAME + ' account)',
      value:  SavedSearchType.SHARED,
      testId: ELEMENT.saveSearchDialogShared,
    },
  ];

  const searchTypeChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
    const newType = e.target.value as SavedSearchType;
    setSavedType(newType);
  };

  const sharedSearchOptionSection = addToOptions.map((option, index) => {
    const id = 'SaveSearchDialog__RadioButtons--Option' + index;
    return (
      <CheckboxRadio
        type="radio"
        id={id}
        key={id}
        label={option.label}
        value={option.value}
        checked={savedType === option.value}
        onChange={searchTypeChangeHandler}
        testId={option.testId}
      />
    );
  });

  const footer = {
    rightItems: [
      <Button
        variant={ButtonVariant.PRIMARY}
        onClick={onSubmit}
        disabled={!savedName.length}
        testId={ELEMENT.saveSearchDialogSave}
      >
        Save
      </Button>,
      <Button variant={ButtonVariant.SECONDARY} onClick={closeAndCleanUp} testId={ELEMENT.saveSearchDialogCancel}>Cancel</Button>,
    ],
  };

  let urlInputFormGroup;
  if (isEdit) {
    urlInputFormGroup = (
      <BootstrapForm.Group controlId={ELEMENT.saveSearchDialogUrl}>
        <BootstrapForm.Label className="bold">URL:</BootstrapForm.Label>
        <BootstrapForm.Control
          as="input"
          type="text"
          value={savedUrl}
          onChange={onUrlChange}
          data-test-id={ELEMENT.saveSearchDialogUrl}
        />
      </BootstrapForm.Group>
    );
  }

  let searchTypeOptionsFormGroup;
  if (hasUserAccessPermission) {
    searchTypeOptionsFormGroup = (
      <BootstrapForm.Group>
        <BootstrapForm.Label className="bold">Save to:</BootstrapForm.Label>
        <br />
        {sharedSearchOptionSection}
      </BootstrapForm.Group>
    );
  }

  const placeholder = savedSearchNames.length > 0 ? 'Enter name, or click to choose existing search from list' : '';

  return (
    <Dialog
      title={dialogTitle}
      show={show}
      close={closeAndCleanUp}
      footer={footer}
      testId={ELEMENT.saveSearchDialog}
    >
      <BootstrapForm className="SaveSearchDialog__Form">
        <BootstrapForm.Group controlId={ELEMENT.saveSearchDialogInput}>
          <BootstrapForm.Label className="bold">Name:</BootstrapForm.Label>
          <ComboBox
            value={savedName}
            onChange={onNameChange}
            onEnter={onSubmit}
            sections={[savedSearchNames]}
            placeholder={placeholder}
            selectOnEnter
            autoFocus
            testId={ELEMENT.saveSearchDialog}
          />
        </BootstrapForm.Group>
        {urlInputFormGroup}
        {searchTypeOptionsFormGroup}
      </BootstrapForm>
    </Dialog>
  );
}

export default SaveSearchDialog;
